import {
  analyserCritere,
  consigneFournisseurCreation,
  consigneFournisseurModification,
  decrireCritere,
  formaterCritere,
  type ReferencePiece,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  type ConsigneFournisseur,
  corps,
  ErreurApi,
  gere,
  lire,
  requeteMoi,
  requetes,
} from '../api'
import { LIBELLES_STATUT_CONSIGNE } from '../libelles'
import { type Champ, Formulaire } from './Formulaire'
import { Ligne, Section, Vide } from './Section'

// Consignes d'un fournisseur à plusieurs endroits (énergie, eau, télécom) : une référence lue
// sur ses pièces (PRM, contrat, adresse du point de livraison…) mène à une entité. Écrites
// ici, elles sont actives ; proposées par l'assistant, elles attendent d'être activées ici.

type Evaluation = { erreurs: string[]; avertissements: string[] }

export const requeteConsignesFournisseur = (tiersId: string) => ({
  queryKey: ['consignes-fournisseur', tiersId],
  queryFn: () => lire(api.tiers[':id'].consignes.$get({ param: { id: tiersId } })),
})

/** Lignes « Contrat : 900001 » vers des critères ; une ligne illisible est une erreur de saisie. */
function versCriteres(lignes: unknown): ReferencePiece[] {
  return ((lignes as string[] | null) ?? []).map((l) => {
    const c = analyserCritere(l)
    if (!c)
      throw new ErreurApi(`Ligne illisible : « ${l} ».`, 400, [
        {
          champ: 'criteres',
          message: 'Une par ligne, « type : valeur » (PRM, PCE, Contrat, Client, Ligne, Adresse).',
        },
      ])
    return c
  })
}

const champsConsigne = (entites: { valeur: string; libelle: string }[]): Champ[] => [
  { nom: 'entiteId', libelle: 'Entité', type: 'choix', requis: true, options: entites },
  {
    nom: 'criteres',
    libelle: 'Critères',
    type: 'liste',
    aide: 'Un par ligne, comparés sans IA : « Contrat : 900001 », « PRM : 0912… », « Adresse : 14 rue des Lilas ». L’un suffit.',
  },
  {
    nom: 'texte',
    libelle: 'Texte libre',
    type: 'zone',
    lignes: 2,
    aide: 'Facultatif, télégraphique : lu par Claude seulement si aucun critère ne tranche.',
  },
]

function Remarques({ e }: { e: Evaluation | null }) {
  if (!e || e.avertissements.length === 0) return null
  return (
    <ul className="discret">
      {e.avertissements.map((a) => (
        <li key={a}>{a}</li>
      ))}
    </ul>
  )
}

/** Section « Consignes » de la fiche d'un fournisseur : lire, ajouter, modifier, retirer. */
export function SectionConsignesFournisseur({ tiersId }: { tiersId: string }) {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const consignes = useQuery(requeteConsignesFournisseur(tiersId))
  const entites = useQuery(requetes.entites).data ?? []
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)
  const gerees = entites
    .filter((e) => !e.archivee && gere(moi, e.id))
    .map((e) => ({ valeur: e.id, libelle: e.nom }))
  const rafraichir = () => client.invalidateQueries({ queryKey: ['consignes-fournisseur'] })
  const aActiver = (consignes.data ?? []).filter((c) => c.statut === 'proposee').length

  return (
    <Section
      titre="Consignes"
      libelleAjout="Nouvelle consigne"
      ajout={
        gerees.length > 0
          ? (fermer) => (
              <Formulaire
                champs={champsConsigne(gerees)}
                onAnnuler={fermer}
                onValider={async (v) => {
                  const r = await lire(
                    api.tiers[':id'].consignes.$post({
                      param: { id: tiersId },
                      json: corps(consigneFournisseurCreation, {
                        ...v,
                        criteres: versCriteres(v.criteres),
                      }),
                    }),
                  )
                  setEvaluation(r.evaluation)
                  await rafraichir()
                  fermer()
                }}
              />
            )
          : undefined
      }
    >
      <p className="discret">
        Pour un fournisseur qui dessert plusieurs endroits : chaque consigne mène ses pièces à une
        entité, d’après une référence lue sur la pièce. Une pièce de ce fournisseur qu’aucune
        consigne ne couvre (nouvel endroit) reste à vérifier, jamais rangée d’office. Une consigne
        ne fait que rattacher.
      </p>
      {aActiver > 0 && (
        <p className="alerte">
          {aActiver} consigne(s) proposée(s) par l’assistant : à relire puis activer.
        </p>
      )}
      <Remarques e={evaluation} />
      {consignes.isPending && <Vide>Chargement…</Vide>}
      {consignes.data?.length === 0 && <Vide>Aucune consigne : une seule entité par défaut.</Vide>}
      {consignes.data?.map((c) => (
        <LigneConsigneFournisseur key={c.id} c={c} gerees={gerees} onEvaluation={setEvaluation} />
      ))}
    </Section>
  )
}

/**
 * Une consigne de fournisseur : critères, entité, statut ; modifier, activer, retirer. Sur la
 * fiche du tiers, et dans Réglages (`avecTiers` : le fournisseur, avec un lien vers sa fiche).
 */
export function LigneConsigneFournisseur({
  c,
  gerees,
  avecTiers = false,
  onEvaluation,
}: {
  c: ConsigneFournisseur
  gerees: { valeur: string; libelle: string }[]
  avecTiers?: boolean
  onEvaluation?: (e: Evaluation | null) => void
}) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const modifiable = gerees.some((e) => e.valeur === c.entiteId)
  const rafraichir = () => client.invalidateQueries({ queryKey: ['consignes-fournisseur'] })
  const agir = async (action: 'activer' | 'desactiver') => {
    try {
      setErreur(null)
      await lire(
        api['consignes-fournisseur'][':id'].action.$post({ param: { id: c.id }, json: { action } }),
      )
      await rafraichir()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Action impossible.')
    }
  }
  return (
    <Ligne
      edition={
        modifiable
          ? (fermer) => (
              <Formulaire
                champs={champsConsigne(gerees)}
                initial={{ ...c, criteres: c.criteres.map(formaterCritere) }}
                onAnnuler={fermer}
                onValider={async (v) => {
                  const r = await lire(
                    api['consignes-fournisseur'][':id'].$patch({
                      param: { id: c.id },
                      json: corps(consigneFournisseurModification, {
                        ...v,
                        criteres: versCriteres(v.criteres),
                      }),
                    }),
                  )
                  onEvaluation?.(r.evaluation)
                  await rafraichir()
                  fermer()
                }}
              />
            )
          : undefined
      }
      onSupprimer={
        modifiable && c.utilisations === 0
          ? async () => {
              await lire(api['consignes-fournisseur'][':id'].$delete({ param: { id: c.id } }))
              await rafraichir()
            }
          : undefined
      }
    >
      {avecTiers && (
        <div>
          <Link to="/tiers/$id" params={{ id: c.tiersId }}>
            {c.tiers}
          </Link>
        </div>
      )}
      <strong>
        {c.criteres.length > 0 ? c.criteres.map(decrireCritere).join(' ou ') : 'Texte libre'} →{' '}
        {c.entite}
      </strong>{' '}
      {c.statut !== 'active' && <span className="badge">{LIBELLES_STATUT_CONSIGNE[c.statut]}</span>}
      {c.viaAssistant && <span className="badge">Proposée par l’assistant</span>}
      {c.texte && <div className="discret">Texte : {c.texte}</div>}
      <div className="discret">
        {c.utilisations === 0
          ? 'N’a encore rattaché aucune pièce.'
          : `A rattaché ${c.utilisations} pièce(s), la dernière le ${new Date(c.utiliseeLe!).toLocaleDateString('fr-FR')}.`}
      </div>
      {modifiable && (
        <div className="boutons">
          {c.statut !== 'active' && (
            <button type="button" className="bouton" onClick={() => void agir('activer')}>
              Activer
            </button>
          )}
          {c.statut !== 'inactive' && c.utilisations > 0 && (
            <button
              type="button"
              className="bouton-discret"
              onClick={() => void agir('desactiver')}
            >
              Retirer
            </button>
          )}
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </Ligne>
  )
}
